import { useId, useState } from 'react'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { Field, fieldErrorId } from '../../platform/primitives/Field'
import { cleanTag } from '../../platform/primitives/TagEditor'
import { collidingTag, TagNameTakenError } from './tagRules'
import { canAddress, TagChangeError, useRenameTag } from './useTagMutations'
import './tags.css'

const plural = (n: number) => `${n} transaction${n === 1 ? '' : 's'}`

export function RenameTagDialog({ name, tags, onClose }: { name: string; tags: readonly string[]; onClose: () => void }) {
  const [value, setValue] = useState(name)
  const [failure, setFailure] = useState<string | null>(null)
  // A hit from the fresh re-check, which the on-screen list had missed. Keyed to the value it refused.
  const [taken, setTaken] = useState<{ value: string; existing: string } | null>(null)
  const rename = useRenameTag()
  const toast = useToast()
  const fieldId = useId()

  const cleaned = cleanTag(value)
  const existing =
    cleaned === ''
      ? undefined
      : (collidingTag(tags, name, cleaned) ?? (taken?.value === cleaned ? taken.existing : undefined))
  const collision = existing === undefined ? undefined : `A tag named "${existing}" already exists.`
  const reason =
    cleaned === ''
      ? "Tag name can't be blank."
      : cleaned === name
        ? 'Type a new name to rename it.'
        : !canAddress(cleaned)
          ? '"." and ".." can\'t be used as tag names.'
          : null
  const describedBy = collision !== undefined ? fieldErrorId(fieldId) : reason !== null ? `${fieldId}-hint` : undefined
  const blocked = reason !== null || collision !== undefined

  async function submit() {
    if (rename.isPending || blocked) return
    setFailure(null)
    try {
      const result = await rename.mutateAsync({ from: name, to: cleaned })
      toast.show({ kind: 'success', message: `Renamed to “${cleaned}” on ${plural(result.updated)}` })
      onClose()
    } catch (thrown) {
      if (thrown instanceof TagNameTakenError) {
        setTaken({ value: cleaned, existing: thrown.existing })
        return
      }
      setFailure(
        thrown instanceof TagChangeError && thrown.status === 404
          ? 'That tag is no longer on any of your transactions. The list has been refreshed.'
          : "The tag wasn't renamed, and nothing has changed. Try again.",
      )
    }
  }

  return (
    <Overlay open onClose={onClose} surface="glass" placement="sheet" label={`Rename “${name}”`}>
      <form
        className="tags-dialog"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <h3 className="tags-dialog__title">{`Rename “${name}”`}</h3>
        <p className="tags-dialog__note">
          {`Changes it on every transaction tagged “${name}”. Auto-rules that use “${name}” keep the old name.`}
        </p>
        <Field
          id={fieldId}
          label="New name"
          size="form"
          value={value}
          error={collision}
          hint={collision === undefined ? (reason ?? undefined) : undefined}
          onChange={(e) => {
            setValue(e.target.value)
            setFailure(null)
          }}
        />
        {failure !== null && (
          <p className="tags-dialog__error" role="alert">
            {failure}
          </p>
        )}
        <div className="tags-dialog__actions">
          <Button type="button" variant="chip" size="form" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="form"
            aria-busy={rename.isPending}
            aria-disabled={blocked || undefined}
            aria-describedby={blocked ? describedBy : undefined}
          >
            {rename.isPending ? 'Renaming…' : 'Rename'}
          </Button>
        </div>
      </form>
    </Overlay>
  )
}
